import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import DateInput from '../../../Componentes/DateInput';
import Espaçador from '../../../Componentes/Espaçador';
import SelectInput from '../../../Componentes/SelectInput';
import TabelaComponente from '../../../Componentes/TabelaComponente';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import { StoreContexto } from '../../../Contexto/Provider';
import { apiService } from '../../../Services/ApiService';
import { useTranslation } from 'react-i18next';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import '../../../Estilos/Fontes.css';
import './ListaRelatorio.css';

export default function ListaRelatorio() {
  const [readings, setReadings] = useState([]);
  const [totalReadings, setTotalReadings] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    environmentId: { value: '', label: 'Todos' },
    sensorId: { value: '', label: 'Todos' },
    date: '',
    timePeriod: { value: '', label: 'Todos' },
  });

  const [filtersOptions, setFiltersOptions] = useState({
    environment: [{ value: '', label: 'Todos' }],
    sensor: [{ value: '', label: 'Todos' }],
    timePeriod: [],
  });

  const [page, setPage] = useState(0);

  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });

  const periods = [
    { value: '', label: 'Todos' },
    { value: 'daily', label: 'Diário' },
    { value: 'last_7_days', label: 'Últimos 7 dias' },
    { value: 'last_15_days', label: 'Últimos 15 dias' },
    { value: 'last_30_days', label: 'Últimos 30 dias' },
  ];

  const navigate = useNavigate();
  const { get, getFile } = apiService();
  const { auth } = useContext(StoreContexto);
  const { t: tVar } = useTranslation('variables');
  const { t: tFilters } = useTranslation('filters');

  useEffect(() => {
    getReadings();
    getEnvironments();

    if (filters.environmentId.value) {
      getSensorsByEnvironment(filters.environmentId.value);
    }
  }, [page, filters]);

  const getEnvironments = async () => {
    try {
      const response = await get('environments/all');
      if (!response) return;

      const result = [{ value: '', label: 'Todos' }].concat(
        response.map((environment) => {
          return { value: environment.id, label: environment.name };
        })
      );

      setFiltersOptions((prevState) => ({
        ...prevState,
        environment: result,
      }));
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao acessar a relação de ambientes' })
    }
  };

  const getSensorsByEnvironment = async (environmentId) => {
    try {
      const response = await get(`sensors/environment/${environmentId}`);
      if (!response) return;

      const result = [{ value: '', label: 'Todos' }].concat(
        response.map((sensor) => {
          return { value: sensor.id, label: sensor.name };
        })
      );

      setFiltersOptions((prevState) => ({
        ...prevState,
        sensor: result,
      }));
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao acessar a relação de sensores' })
    }
  };

  const getReadings = async () => {
    const activeFilters = Object.entries(filters)
      .filter(([key, value]) => value.value)
      .reduce((acc, [key, value]) => {
        acc[key] = value.value;
        return acc;
      }, {});

    const queryParams = { page, limit: 10, ...activeFilters };

    if (filters.date) {
      queryParams.date = new Date(filters.date).toISOString().split('T')[0];
    }

    setIsLoading(true);
    try {
      const response = await get('readings', queryParams);
      const result = response.data.map((reading) => {
        reading.type = tFilters(reading.type.toUpperCase());
        reading.date = new Date(reading.timestamp).toLocaleDateString();
        reading.time = new Date(reading.timestamp).toLocaleTimeString();
        if (reading.temperature === null) reading.temperature = '-';
        else reading.temperature = `${reading.temperature} °C`;

        if (reading.humidity === null) reading.humidity = '-';
        else reading.humidity = `${reading.humidity} %`;

        if (reading.energy === true) reading.energy = 'Sim';
        else if (reading.energy === false) reading.energy = 'Não';
        else reading.energy = '-';

        if (reading.endOfCourse === true) reading.endOfCourse = 'Aberta';
        else if (reading.endOfCourse === false) reading.endOfCourse = 'Fechada';
        else reading.endOfCourse = '-';

        return reading;
      });

      setReadings(result || []);
      setTotalReadings(response.total || 0);
    } catch (error) {
      setModalError({ visible: true, message: 'Não foi possível acessar os dados de relatório '})
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = async (filter, value) => {
    let newFilters = { ...filters };
    newFilters[filter] = value;
    if (filter === 'environmentId') {
      newFilters.sensorId = { value: '', label: 'Todos'};
      newFilters.timePeriod = { value: '', label: 'Todos' };
      setFiltersOptions((prevState) => ({
        ...prevState,
        sensor: [{ value: '', label: 'Todos' }],
      }));
    } else if (filter === 'date') {
      newFilters.timePeriod = { value: '' };
    }

    setFilters(newFilters);
  };

  const handleSelectPeriod = async (period) => {
    let newFilters = { ...filters };
    newFilters.timePeriod = period;
    newFilters.date = '';
    setFilters(newFilters);
  };

  const nextPage = async () => {
    setPage(page + 1);
  };

  const previousPage = async () => {
    setPage(page - 1);
  };

  const downloadCsv = async () => {
    const activeFilters = Object.entries(filters)
      .filter(([key, value]) => value.value)
      .reduce((acc, [key, value]) => {
        acc[key] = value.value;
        return acc;
      }, {});

    const queryParams = { ...activeFilters };

    if (filters.date) {
      queryParams.date = new Date(filters.date).toISOString().split('T')[0];
    }

    try {
      const blob = await getFile('readings/csv', queryParams);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Relatório-${new Date().toISOString().replace(/:/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao baixar arquivo' });
    }
  };

  return (
    <CorpoPagina loading={isLoading}>
    {modalError.visible && (
      <ModalConfirmacao
        aviso={modalError.message}
        erro={true}
        clicado={() => setModalError({ visible: false, message: '' })}
      />
    )}
      <div className="ContainerGeral">
        <TopicoTexto titulo="Lista de Leituras" altura={50} />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 48,
            marginBottom: 16,
          }}
        >
          <SelectInput
            titulo="Ambiente"
            padrao={filters.environmentId}
            itens={filtersOptions.environment}
            largura={200}
            clicado={(value) => handleFilter('environmentId', value)}
          />
          <Espaçador espaço={16} />
          <SelectInput
            titulo="Sensor"
            padrao={filters.sensorId}
            itens={filtersOptions.sensor}
            largura={200}
            clicado={(value) => handleFilter('sensorId', value)}
            disabled={!filters.environmentId.value}
          />
          <Espaçador espaço={16} />
          <DateInput
            titulo="Data"
            largura={200}
            top={9}
            clicado={(date) => handleFilter('date', date)}
          />
          <Espaçador espaço={16} />
          <SelectInput
            titulo="Período"
            padrao={filters.timePeriod}
            itens={periods}
            largura={200}
            clicado={handleSelectPeriod}
          />
          <Espaçador espaço={16} />
          <button
            className="csv-button"
            onClick={downloadCsv}
            disabled={
              filters.environmentId.value === '' ||
              filters.timePeriod.value === ''
            }
          >CSV</button>
        </div>
        <TabelaComponente
          colunas={[
            'AMBIENTE',
            'TIPO',
            'TEMPERATURA',
            'UMIDADE',
            'ENERGIA',
            'PORTA',
            'DATA',
            'HORA',
          ]}
          campos={[
            'environmentName',
            'type',
            'temperature',
            'humidity',
            'energy',
            'endOfCourse',
            'date',
            'time',
          ]}
          linhas={readings}
          paginaAtual={page}
          total={totalReadings}
          aviso={!readings.length ? 'Nenhuma leitura encontrada' : 'Buscando leituras...'}
          actions={false}
          onMenuItemSelect={() => {}}
          goToNextPage={() => nextPage()}
          goToPreviousPage={() => previousPage()}
          entity='readings'
        />
      </div>
    </CorpoPagina>
  );
}
